import React, { useState, useRef, useMemo } from 'react';
import { View, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import Card from './Card';
import { getCarouselGap, getCarouselSidePadding } from '../../utils/spacing';
import ChevronLeftIcon from '../icons/ChevronLeftIcon';
import ChevronRightIcon from '../icons/ChevronRightIcon';

type SimpleWebCarouselProps = {
  data: any[];
  selected: number;
  onSnapToItem: (index: number) => void;
  itemWidth: number;
  onCardPress?: () => void;
};

/**
 * Dead simple carousel for web - no ScrollView, just absolute positioning
 */
const SimpleWebCarousel: React.FC<SimpleWebCarouselProps> = ({
  data,
  selected,
  onSnapToItem,
  itemWidth,
  onCardPress
}) => {
  const [currentIndex, setCurrentIndex] = useState(selected);
  const { width } = useWindowDimensions();
  const touchStartX = useRef(0);

  // Calculate responsive gap and padding
  const gap = useMemo(() => getCarouselGap(width), [width]);
  const sidePadding = useMemo(() => getCarouselSidePadding(width, itemWidth), [width, itemWidth]);

  const goToIndex = (index: number) => {
    setCurrentIndex(index);
    onSnapToItem(index);
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      goToIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < data.length - 1) {
      goToIndex(currentIndex + 1);
    }
  };

  // Simple swipe detection
  const handleTouchStart = (e: any) => {
    touchStartX.current = e.nativeEvent.pageX || e.nativeEvent.touches?.[0]?.pageX || 0;
  };

  const handleTouchEnd = (e: any) => {
    const touchEndX = e.nativeEvent.pageX || e.nativeEvent.changedTouches?.[0]?.pageX || 0;
    const diff = touchStartX.current - touchEndX;

    // Swipe threshold: 50px
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        // Swiped left -> go next
        goToNext();
      } else {
        // Swiped right -> go previous
        goToPrevious();
      }
    }
  };

  return (
    <View style={styles.container}>
      {/* Card container with overflow hidden */}
      <View
        style={styles.cardsContainer}
      >
        {/* Sliding track - moves horizontally */}
        <View
          style={[
            styles.track,
            {
              // @ts-ignore - CSS transform for web
              transform: `translateX(calc(-${currentIndex} * (${itemWidth}px + ${gap}px) + ${sidePadding}px))`,
              transition: 'transform 0.3s ease-out',
            } as any,
          ]}
        >
          {data.map((item, index) => (
            <View
              key={index}
              style={[
                styles.cardWrapper,
                {
                  width: itemWidth,
                  height: '100%',
                  marginRight: index < data.length - 1 ? gap : 0,
                },
              ]}
            >
              <TouchableOpacity
                activeOpacity={0.9}
                onPress={() => {
                  if (index === currentIndex && onCardPress) {
                    onCardPress();
                  }
                }}
                style={{ width: '100%', height: '100%' }}
              >
                <Card story={item} selected={index === currentIndex} />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      </View>

      {/* Navigation arrows */}
      {currentIndex > 0 && (
        <TouchableOpacity
          style={[styles.arrow, styles.arrowLeft]}
          onPress={goToPrevious}
          activeOpacity={0.7}
        >
          <View style={styles.arrowButton}>
            <ChevronLeftIcon size={24} color="#333" />
          </View>
        </TouchableOpacity>
      )}

      {currentIndex < data.length - 1 && (
        <TouchableOpacity
          style={[styles.arrow, styles.arrowRight]}
          onPress={goToNext}
          activeOpacity={0.7}
        >
          <View style={styles.arrowButton}>
            <ChevronRightIcon size={24} color="#333" />
          </View>
        </TouchableOpacity>
      )}

      {/* Pagination dots */}
      <View style={styles.pagination}>
        {data.map((_, index) => (
          <TouchableOpacity
            key={index}
            onPress={() => goToIndex(index)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <View
              style={[
                styles.dot,
                index === currentIndex && styles.dotActive,
              ]}
            />
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  cardsContainer: {
    width: '100%',
    height: '100%',
    // @ts-ignore
    overflow: 'hidden',
  },
  track: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    height: '100%',
  },
  cardWrapper: {
    // Cards are laid out horizontally in a row
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    // @ts-ignore
    transform: 'translateY(-50%)',
    zIndex: 10,
    // @ts-ignore
    cursor: 'pointer',
  },
  arrowLeft: {
    left: 10,
  },
  arrowRight: {
    right: 10,
  },
  arrowButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
  },
  pagination: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
    // @ts-ignore
    gap: 8,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    // @ts-ignore
    cursor: 'pointer',
  },
  dotActive: {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    width: 24,
  },
});

export default SimpleWebCarousel;
